
import { useEffect, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Building2, User, Camera, Image, PoundSterling, FileText, Loader2, Edit, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CustomerDealCalculator } from "@/components/crm/CustomerDealCalculator";
import { ProposalAcceptanceSection } from "@/components/common/ProposalAcceptanceSection";
import { useQueryClient } from "@tanstack/react-query";

export const CustomerProposal = () => {
  const [searchParams] = useSearchParams();
  const { token } = useParams();
  const [deal, setDeal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [acceptanceReady, setAcceptanceReady] = useState(false);
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState<string | null>(null);
  const [orderReference, setOrderReference] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const dealId = searchParams.get('dealId');
  const mode = searchParams.get('mode');

  console.log('CustomerProposal params:', { dealId, token, mode });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  useEffect(() => {
    const fetchDeal = async () => {
      if (!dealId || !token) {
        console.log('Missing dealId or token:', { dealId, token });
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching deal with ID:', dealId);
        
        // First, verify the token against the proposal notification
        console.log('Verifying token:', token);
        const { data: notificationData, error: notificationError } = await supabase
          .from('proposal_notifications')
          .select('access_token, deal_id, contact_id')
          .eq('deal_id', dealId)
          .eq('access_token', token)
          .maybeSingle();

        console.log('Notification verification result:', { notificationData, notificationError });

        if (notificationError) {
          console.error('Error verifying token:', notificationError);
          throw new Error('Error verifying access token');
        }

        if (!notificationData) {
          console.error('No matching proposal notification found for token and deal ID');
          throw new Error('Invalid access token or proposal not found');
        }

        console.log('Token verified successfully, fetching deal details...');
        
        const { data, error } = await supabase
          .from('crm_deals')
          .select(`
            *,
            contact:profiles!crm_deals_contact_id_fkey(first_name, last_name, email),
            organization:organizations(name, company_id),
            stage:crm_deal_stages(name, color),
            assigned_staff:crm_staff!crm_deals_assigned_to_staff_id_fkey(first_name, last_name, email),
            assigned_partner:crm_partners!crm_deals_assigned_to_partner_id_fkey(first_name, last_name, email)
          `)
          .eq('id', dealId)
          .maybeSingle();

        console.log('Deal query result:', { data, error });

        if (error) {
          console.error('Error fetching deal:', error);
          throw error;
        }

        if (!data) {
          console.error('No deal found with ID:', dealId);
          throw new Error('Deal not found');
        }

        console.log('Fetched deal data:', data);
        console.log('Contact email from deal:', data.contact?.email);

        setDeal(data);
        
        if (data.status === 'won') {
          setAccepted(true);
        }

        if (mode === 'edit') {
          setIsEditing(true);
          setCalculatorOpen(true);
        }
      } catch (error) {
        console.error('Error fetching deal:', error);
        toast({
          title: "Error",
          description: "Unable to load proposal details. Please check the link or contact support.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDeal();
  }, [dealId, token, mode, toast]);

  const handleAcceptProposal = async () => {
    if (!dealId || accepting || !acceptanceReady || !selectedDeliveryAddressId) return;

    setAccepting(true);
    try {
      const { error } = await supabase.functions.invoke('proposal-accept', {
        body: { 
          dealId, 
          contactEmail: deal.contact?.email,
          deliveryAddressId: selectedDeliveryAddressId,
          orderReference: orderReference
        }
      });

      if (error) throw error;

      // Send order confirmation email to orders@snapshot.live
      try {
        console.log('📧 Sending order confirmation to orders@snapshot.live');
        
        // Find the order/sale associated with this deal
        const { data: saleData } = await supabase
          .from('sales')
          .select(`
            id,
            total_amount,
            order_reference,
            orders!inner (
              id,
              order_reference
            )
          `)
          .eq('deal_id', dealId)
          .single();

        if (saleData && saleData.orders && saleData.orders.length > 0) {
          const firstOrder = saleData.orders[0];
          // Send notification email to internal team without PDF
          await supabase.functions.invoke('send-order-confirmation', {
            body: {
              dealId: dealId,
              orderId: firstOrder.id,
              contactEmail: 'orders@snapshot.live', // Send to internal team only
              internalNotification: true, // Flag to skip PDF generation
              proposalAccepted: true // Flag to indicate this is from proposal acceptance
            }
          });
          
          console.log('✅ Order confirmation sent to orders@snapshot.live');
        }
      } catch (emailError) {
        console.error('⚠️ Failed to send order confirmation email:', emailError);
        // Don't fail the whole operation for email issues
      }

      setAccepted(true);
      
      queryClient.invalidateQueries({ queryKey: ['proposal-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      
      toast({
        title: "Thank you for your order!",
        description: "You can view the progress of your order under the Orders page.",
      });
    } catch (error) {
      console.error('Error accepting proposal:', error);
      toast({
        title: "Error",
        description: "Unable to accept proposal. Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleEditProposal = () => {
    setIsEditing(true);
    setCalculatorOpen(true);
  };

  const handleCalculatorAccept = async (updatedData: any) => {
    if (!acceptanceReady || !selectedDeliveryAddressId) {
      toast({
        title: "Requirements Not Met",
        description: "Please select a delivery address and acknowledge the terms and conditions before accepting.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error: updateError } = await supabase.functions.invoke('update-customer-proposal', {
        body: {
          dealId,
          contactEmail: deal.contact?.email,
          updatedData,
          originalDealData: deal
        }
      });

      if (updateError) throw updateError;

      const { error: acceptError } = await supabase.functions.invoke('proposal-accept', {
        body: { 
          dealId, 
          contactEmail: deal.contact?.email,
          deliveryAddressId: selectedDeliveryAddressId,
          orderReference: orderReference
        }
      });

      if (acceptError) throw acceptError;

      // Send order confirmation email to orders@snapshot.live after successful acceptance
      try {
        console.log('📧 Sending order confirmation to orders@snapshot.live');
        
        // Find the order/sale associated with this deal
        const { data: saleData } = await supabase
          .from('sales')
          .select(`
            id,
            total_amount,
            order_reference,
            orders!inner (
              id,
              order_reference
            )
          `)
          .eq('deal_id', dealId)
          .single();

        if (saleData && saleData.orders && saleData.orders.length > 0) {
          const firstOrder = saleData.orders[0];
          // Send notification email to internal team without PDF
          await supabase.functions.invoke('send-order-confirmation', {
            body: {
              dealId: dealId,
              orderId: firstOrder.id,
              contactEmail: 'orders@snapshot.live', // Send to internal team only
              internalNotification: true, // Flag to skip PDF generation
              proposalAccepted: true // Flag to indicate this is from proposal acceptance
            }
          });
          
          console.log('✅ Order confirmation sent to orders@snapshot.live');
        }
      } catch (emailError) {
        console.error('⚠️ Failed to send order confirmation email:', emailError);
        // Don't fail the whole operation for email issues
      }

      setAccepted(true);
      setCalculatorOpen(false);
      setIsEditing(false);

      queryClient.invalidateQueries({ queryKey: ['proposal-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['deals'] });

      toast({
        title: "Thank you for your order!",
        description: "Your proposal has been updated and accepted. You can view the progress of your order under the Orders page.",
      });
    } catch (error) {
      console.error('Error accepting updated proposal:', error);
      toast({
        title: "Error",
        description: "Unable to accept proposal. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSaveAndClose = async (updatedData: any) => {
    try {
      const { error } = await supabase.functions.invoke('update-customer-proposal', {
        body: {
          dealId,
          contactEmail: deal.contact?.email,
          updatedData,
          originalDealData: deal
        }
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['proposal-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['deals'] });

      toast({
        title: "Changes Saved",
        description: "Your changes have been saved successfully.",
      });

      setCalculatorOpen(false);
      setIsEditing(false);

      if (mode === 'edit') {
        window.close();
        
        setTimeout(() => {
          if (!window.closed) {
            window.location.href = '/notifications';
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error saving proposal:', error);
      toast({
        title: "Error",
        description: "Unable to save changes. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAcceptanceReady = (ready: boolean, deliveryAddressId: string | null, orderReference: string | null, missing: string[] = []) => {
    setAcceptanceReady(ready);
    setSelectedDeliveryAddressId(deliveryAddressId);
    setOrderReference(orderReference);
    setMissingFields(missing);
  };

  const getAssigneeInfo = () => {
    if (deal?.assigned_staff) {
      return {
        name: `${deal.assigned_staff.first_name} ${deal.assigned_staff.last_name}`,
        email: deal.assigned_staff.email,
        type: 'Staff Member'
      };
    } else if (deal?.assigned_partner) {
      return {
        name: `${deal.assigned_partner.first_name} ${deal.assigned_partner.last_name}`,
        email: deal.assigned_partner.email,
        type: 'Partner'
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
          <span className="text-slate-600">Loading proposal...</span>
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <h1 className="text-xl font-semibold text-slate-800 mb-2">Proposal Not Found</h1>
            <p className="text-slate-600">The proposal you're looking for could not be found or the access link has expired.</p>
            <p className="text-sm text-slate-500 mt-4">
              If you believe this is an error, please contact our support team.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const assignee = getAssigneeInfo();

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Photography Services Proposal</h1>
          <p className="text-slate-600">
            {mode === 'edit' ? 'Edit and customize your service package' : 'Review and accept your customized service package'}
          </p>
        </div>

        {accepted && (
          <div className="mb-6">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 text-green-800">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-semibold">Proposal Accepted!</span>
                </div>
                <p className="text-green-700 mt-1">Thank you for choosing our services. We'll be in touch shortly to begin the process.</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Deal Information */}
        <Card className="mb-6">
          <CardHeader className="bg-gradient-to-r from-teal-primary to-teal-dark text-white">
            <CardTitle className="text-xl flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {deal.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Client Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-teal-600" />
                    <span>{deal.contact?.first_name} {deal.contact?.last_name}</span>
                  </div>
                  {deal.organization && (
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-teal-600" />
                      <span>{deal.organization.name}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {assignee && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Your Representative</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-teal-600" />
                      <span>{assignee.name}</span>
                    </div>
                    <Badge variant="outline" className="border-teal-300 text-teal-700">
                      {assignee.type}
                    </Badge>
                    {assignee.email && (
                      <div className="text-sm text-teal-600">{assignee.email}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Service Package */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center justify-between">
              Service Package Details
              {!accepted && mode !== 'edit' && (
                <Button
                  onClick={handleEditProposal}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Customize
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <Camera className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                <h4 className="font-semibold text-slate-800">Professional Cameras</h4>
                <p className="text-2xl font-bold text-slate-800 mt-2">{deal.total_cameras || 0}</p>
                {(deal.deal_data?.commitment_years === 5 || deal.deal_data?.camera_price === 0) && deal.deal_data?.plan !== 'Trial' && (
                  <span className="inline-flex items-center mt-2 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-800 uppercase tracking-wide">
                    Camera Free
                  </span>
                )}
              </div>
              
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <Image className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                <h4 className="font-semibold text-slate-800">Image Credits</h4>
                <p className="text-2xl font-bold text-slate-800 mt-2">{(deal.total_images || 0).toLocaleString()}</p>
              </div>
              
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <PoundSterling className="h-8 w-8 text-teal-600 mx-auto mb-2" />
                <h4 className="font-semibold text-slate-800">Total Investment</h4>
                <p className="text-2xl font-bold text-slate-800 mt-2">{formatCurrency(deal.value || 0)}</p>
              </div>
            </div>

            {/* Price Breakdown */}
            {deal.deal_data && (deal.deal_data.subtotal || deal.discount_amount > 0 || deal.deal_data.payment_structure) && (
              <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-3">Price Breakdown</h4>
                {((deal.deal_data?.commitment_years === 5 || deal.deal_data?.camera_price === 0) && deal.deal_data?.plan !== 'Trial' || deal.discount_amount > 0) && (
                  <div className="mb-3 p-3 rounded-md bg-green-50 border border-green-300">
                    <div className="text-sm font-semibold text-green-800 mb-1">Discount applied to your order</div>
                    <div className="text-xs text-green-700 space-y-0.5">
                      {(deal.deal_data?.commitment_years === 5 || deal.deal_data?.camera_price === 0) && deal.deal_data?.plan !== 'Trial' && (
                        <div>
                          {deal.total_cameras || 0}x camera{(deal.total_cameras || 0) > 1 ? 's' : ''} included free
                          {deal.deal_data?.commitment_years === 5 ? ' (5-year commitment)' : ''}
                        </div>
                      )}
                      {deal.discount_amount > 0 && (
                        <div>Additional discount: −{formatCurrency(deal.discount_amount)}</div>
                      )}
                    </div>
                  </div>
                )}
                <div className="space-y-2 text-sm">
                  {deal.deal_data.payment_structure && (
                    <div className="flex justify-between font-medium text-slate-800">
                      <span>Payment Structure:</span>
                      <span>{deal.deal_data.payment_structure === 'annual' ? 'Annual Contract' : 'Upfront Payment'}</span>
                    </div>
                  )}
                  
                  {deal.deal_data.payment_structure === 'annual' && deal.deal_data.annual_payment_breakdown ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Year 1 Payment:</span>
                        <span className="font-medium text-slate-800">{formatCurrency(deal.deal_data.annual_payment_breakdown.year1_payment)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Annual Payment (Years 2-{deal.deal_data.annual_payment_breakdown.contract_years}):</span>
                        <span className="font-medium text-slate-800">{formatCurrency(deal.deal_data.annual_payment_breakdown.subsequent_annual_payment)} per year</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Contract Duration:</span>
                        <span className="font-medium text-slate-800">{deal.deal_data.annual_payment_breakdown.contract_years} years minimum</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 font-semibold text-slate-800">
                        <span>Total Contract Value:</span>
                        <span>{formatCurrency(deal.deal_data.annual_payment_breakdown.year1_payment + (deal.deal_data.annual_payment_breakdown.subsequent_annual_payment * (deal.deal_data.annual_payment_breakdown.contract_years - 1)))}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      {deal.deal_data.subtotal && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Subtotal:</span>
                          <span className="font-medium text-slate-800">{formatCurrency(deal.deal_data.subtotal)}</span>
                        </div>
                      )}
                      {deal.discount_amount > 0 && (
                        <div className="flex justify-between text-red-600">
                          <span>Discount:</span>
                          <span className="font-medium">-{formatCurrency(deal.discount_amount)}</span>
                        </div>
                      )}
                      {deal.deal_data.vat && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">VAT (20%):</span>
                          <span className="font-medium text-slate-800">{formatCurrency(deal.deal_data.vat)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-2 font-semibold text-slate-800">
                        <span>Total:</span>
                        <span>{formatCurrency(deal.value || 0)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Full Proposal Details (read-only) */}
            {deal.deal_data && (
              <div className="mt-6 p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-3">Full Proposal Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {deal.deal_data.camera_model && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Camera model:</span>
                      <span className="font-medium text-slate-800">{deal.deal_data.camera_model}</span>
                    </div>
                  )}
                  {deal.total_cameras != null && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Quantity:</span>
                      <span className="font-medium text-slate-800">{deal.total_cameras}</span>
                    </div>
                  )}
                  {deal.deal_data.plan && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Plan:</span>
                      <span className="font-medium text-slate-800">{deal.deal_data.plan}</span>
                    </div>
                  )}
                  {deal.deal_data.images_per_day != null && deal.deal_data.plan !== 'Trial' && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Images per day:</span>
                      <span className="font-medium text-slate-800">{deal.deal_data.images_per_day}</span>
                    </div>
                  )}
                  {deal.deal_data.credits_per_camera != null && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Credits per camera / year:</span>
                      <span className="font-medium text-slate-800">{Number(deal.deal_data.credits_per_camera).toLocaleString()}</span>
                    </div>
                  )}
                  {deal.total_images != null && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total credits:</span>
                      <span className="font-medium text-slate-800">{Number(deal.total_images).toLocaleString()}</span>
                    </div>
                  )}
                  {deal.deal_data.price_per_image != null && deal.deal_data.plan !== 'Trial' && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Price per image:</span>
                      <span className="font-medium text-slate-800">£{Number(deal.deal_data.price_per_image).toFixed(2)}</span>
                    </div>
                  )}
                  {deal.deal_data.commitment_years && deal.deal_data.plan !== 'Trial' && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Commitment:</span>
                      <span className="font-medium text-slate-800">{deal.deal_data.commitment_years} years</span>
                    </div>
                  )}
                  {deal.deal_data.payment_structure && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Payment structure:</span>
                      <span className="font-medium text-slate-800">{deal.deal_data.payment_structure === 'annual' ? 'Annual contract' : 'Upfront'}</span>
                    </div>
                  )}
                  {deal.deal_data.custom_ai_model && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Custom AI model:</span>
                      <span className="font-medium text-teal-700">Enabled (+£0.10/image)</span>
                    </div>
                  )}
                  {deal.deal_data.camera_unit_price != null && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Camera unit price:</span>
                      <span className="font-medium text-slate-800">£{Number(deal.deal_data.camera_unit_price).toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {Array.isArray(deal.deal_data.accessories) && deal.deal_data.accessories.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <div className="text-sm font-semibold text-slate-800 mb-2">Accessories</div>
                    <div className="space-y-1 text-sm">
                      {deal.deal_data.accessories.map((a: any, idx: number) => (
                        <div key={idx} className="flex justify-between">
                          <span className="text-slate-600">{a.quantity}x {a.name}</span>
                          <span className="font-medium text-slate-800">{formatCurrency((Number(a.unit_price) || 0) * (Number(a.quantity) || 0))}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* What's Included */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-slate-800">What's Included</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Professional camera equipment setup</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>High-resolution image capture</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Secure cloud storage</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>24/7 technical support</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Detailed reporting & analytics</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Professional image processing</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Proposal Acceptance Section */}
        {!accepted && (
          <div className="mb-8">
            <ProposalAcceptanceSection
              organizationId={deal.organization_id}
              onAcceptanceReady={handleAcceptanceReady}
              isPilot={deal.deal_data?.plan === 'Trial'}
            />
          </div>
        )}

        {/* Action Buttons */}
        {!accepted && (
          <div className="text-center space-y-6">
            <div className="flex justify-center gap-4">
              {mode !== 'edit' && (
                <Button
                  onClick={handleEditProposal}
                  variant="outline"
                  size="lg"
                  className="flex items-center gap-2"
                >
                  <Edit className="h-5 w-5" />
                  Customize Proposal
                </Button>
              )}
              <Button
                onClick={handleAcceptProposal}
                disabled={accepting || !acceptanceReady}
                size="lg"
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 text-lg disabled:opacity-50"
              >
                {accepting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Accept This Proposal
                  </>
                )}
              </Button>
            </div>
            {!acceptanceReady && missingFields.length > 0 && (
              <div className="max-w-md mx-auto text-left text-sm text-muted-foreground border rounded-md p-3 bg-muted/30">
                <div className="font-medium mb-1">Please complete before accepting:</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {missingFields.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-sm text-slate-600">
              By accepting this proposal, you agree to the terms and pricing outlined above.
            </p>
          </div>
        )}

        <CustomerDealCalculator
          open={calculatorOpen}
          onOpenChange={setCalculatorOpen}
          initialData={{
            cameras: deal?.total_cameras || 1,
            credits: deal?.deal_data?.credits_per_camera ?? 1095,
            plan: deal?.deal_data?.plan,
            camera_model_id: deal?.deal_data?.camera_model_id,
            commitment_years: deal?.deal_data?.commitment_years,
            payment_structure: deal?.deal_data?.payment_structure,
            custom_ai_model: deal?.deal_data?.custom_ai_model || false,
            contactName: deal?.contact ? `${deal.contact.first_name} ${deal.contact.last_name}` : undefined,
            organizationName: deal?.organization?.name,
            discount_amount: deal?.discount_amount ?? deal?.deal_data?.discount_amount ?? 0,
            discount_types: deal?.deal_data?.discount_types ?? [],
            accessories: deal?.deal_data?.accessories ?? [],
            originalValue: deal?.value ?? 0
          }}
          onSubmit={handleCalculatorAccept}
          onSaveAndClose={handleSaveAndClose}
          isEditing={isEditing}
        />
      </div>
    </div>
  );
};
